import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);

  const slides = [
    {
      id: 1,
      title: '신앙과 나눔이 함께하는',
      highlight: 'MUST 플랫폼',
      description: '달란트 포인트로 신앙 콘텐츠를 나누고, 교회 커뮤니티에서 소통하며, 필요한 상품을 교환하는 통합 플랫폼',
      image: 'https://public.readdy.ai/ai/img_res/3fa5509369edd6ee2c5e3d11019255ff.jpg',
      solidBackground: false,
      buttons: [
        { text: '커뮤니티 둘러보기', link: '/community', primary: true },
        { text: 'MUST몰 방문하기', link: '/commerce', primary: false }
      ],
      stats: [
        { value: '1,234', label: '활성 회원' },
        { value: '89', label: '교회 커뮤니티' },
        { value: '567', label: '공유 콘텐츠' },
        { value: '2.5M', label: '달란트 사용량' }
      ]
    },
    {
      id: 2,
      title: 'MUST의',
      highlight: '특별한 기능들',
      description: '신앙 생활을 더욱 풍성하게 만들어주는 다양한 기능을 경험해보세요',
      solidBackground: true,
      backgroundColor: 'bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800',
      buttons: [
        { text: '기능 둘러보기', link: '/content', primary: true },
        { text: '지금 시작하기', link: '/auth/signup', primary: false }
      ],
      features: [
        { icon: 'ri-coin-line', title: '달란트 포인트 적립', color: 'bg-orange-500', link: '/talent/intro' },
        { icon: 'ri-shopping-bag-3-line', title: '교회 상품 거래', color: 'bg-orange-500', link: '/commerce' },
        { icon: 'ri-book-open-line', title: '신앙 콘텐츠 공유', color: 'bg-orange-500', link: '/content' },
        { icon: 'ri-team-line', title: '교회 커뮤니티', color: 'bg-orange-500', link: '/community' }
      ]
    },
    {
      id: 3,
      title: '달란트로',
      highlight: '함께 성장하세요',
      description: '활동하고 적립하고 사용하며 신앙 공동체와 함께 성장하는 특별한 경험',
      image: 'https://readdy.ai/api/search-image?query=Christian%20community%20members%20growing%20together%20in%20faith%2C%20people%20sharing%20and%20helping%20each%20other%2C%20warm%20collaborative%20atmosphere%20with%20natural%20lighting%2C%20modern%20church%20setting%20with%20diverse%20group%20of%20believers%2C%20professional%20photography%20with%20soft%20focus%20background%2C%20uplifting%20and%20inspiring%20ambiance&width=1920&height=1080&seq=hero003&orientation=landscape',
      solidBackground: false,
      buttons: [
        { text: '달란트 알아보기', link: '/talent/intro', primary: true },
        { text: '포인트 적립하기', link: '/talent', primary: false }
      ],
      benefits: [
        { icon: 'ri-upload-cloud-line', title: '콘텐츠 업로드', points: '+100P' },
        { icon: 'ri-heart-line', title: '좋아요 & 댓글', points: '+10P' },
        { icon: 'ri-shopping-cart-line', title: '상품 구매', points: '사용' },
        { icon: 'ri-gift-line', title: '포인트 선물', points: '나눔' }
      ]
    }
  ];

  useEffect(() => {
    if (!isPlaying) return;

    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [slides.length, isPlaying]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleFeatureClick = (link: string) => {
    window.REACT_APP_NAVIGATE(link);
  };

  return (
    <section className="relative h-screen flex items-center justify-center overflow-hidden">
      {/* Slides */}
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          {/* Background */}
          {slide.solidBackground ? (
            <div className={`absolute inset-0 w-full h-full ${slide.backgroundColor}`}></div>
          ) : (
            <>
              <div className="absolute inset-0 w-full h-full">
                <img
                  src={slide.image}
                  alt={`Slide ${slide.id}`}
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
              </div>
            </>
          )}

          {/* Content */}
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full h-full flex flex-col items-center justify-center py-20">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight">
              {slide.title}<br />
              <span className={slide.solidBackground ? "text-orange-400" : "text-teal-400"}>{slide.highlight}</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/90 mb-8 sm:mb-12 max-w-3xl mx-auto leading-relaxed px-4">
              {slide.description}
            </p>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-8 sm:mb-16 px-4">
              {slide.buttons.map((button, btnIndex) => (
                <Link
                  key={btnIndex}
                  to={button.link}
                  className={`w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base font-semibold rounded-lg transition-colors shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer ${
                    button.primary
                      ? slide.solidBackground 
                        ? 'bg-white text-teal-700 hover:bg-gray-100'
                        : 'bg-teal-600 text-white hover:bg-teal-700'
                      : 'bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 border-2 border-white/30'
                  }`}
                >
                  {button.text}
                </Link>
              ))}
            </div>

            {/* Stats for Slide 1 */}
            {slide.stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8 max-w-4xl mx-auto px-4">
                {slide.stats.map((stat, statIndex) => (
                  <div key={statIndex} className="text-center">
                    <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-1 sm:mb-2">{stat.value}</div>
                    <div className="text-white/80 text-xs sm:text-sm md:text-base">{stat.label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Features for Slide 2 */}
            {slide.features && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 max-w-4xl mx-auto px-4">
                {slide.features.map((feature, featureIndex) => (
                  <div
                    key={featureIndex}
                    onClick={() => handleFeatureClick(feature.link)}
                    className="bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-white/20 hover:bg-white/20 transition-all cursor-pointer"
                  >
                    <div className={`w-10 h-10 sm:w-14 sm:h-14 ${feature.color} rounded-lg sm:rounded-xl flex items-center justify-center mb-3 sm:mb-4 mx-auto`}>
                      <i className={`${feature.icon} text-xl sm:text-2xl text-white`}></i>
                    </div>
                    <h3 className="text-white font-semibold text-xs sm:text-sm md:text-base">{feature.title}</h3>
                  </div>
                ))}
              </div>
            )}

            {/* Benefits for Slide 3 */}
            {slide.benefits && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 max-w-4xl mx-auto px-4">
                {slide.benefits.map((benefit, benefitIndex) => (
                  <div
                    key={benefitIndex}
                    className="bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-white/20"
                  >
                    <div className="w-10 h-10 sm:w-14 sm:h-14 bg-teal-600 rounded-lg sm:rounded-xl flex items-center justify-center mb-3 sm:mb-4 mx-auto">
                      <i className={`${benefit.icon} text-xl sm:text-2xl text-white`}></i>
                    </div>
                    <h3 className="text-white font-semibold text-xs sm:text-sm md:text-base mb-1 sm:mb-2">{benefit.title}</h3>
                    <p className="text-teal-400 font-bold text-xs sm:text-sm">{benefit.points}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-2 sm:left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-full flex items-center justify-center transition-colors cursor-pointer"
        aria-label="Previous slide"
      >
        <i className="ri-arrow-left-s-line text-white text-xl sm:text-2xl"></i>
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-2 sm:right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-full flex items-center justify-center transition-colors cursor-pointer"
        aria-label="Next slide"
      >
        <i className="ri-arrow-right-s-line text-white text-xl sm:text-2xl"></i>
      </button>

      {/* Bottom Controls */}
      <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex items-center gap-3 sm:gap-6">
        {/* Dots Navigation */}
        <div className="flex items-center gap-2 sm:gap-3">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all cursor-pointer ${
                index === currentSlide
                  ? 'w-8 sm:w-12 h-2 sm:h-3 bg-teal-400'
                  : 'w-2 sm:w-3 h-2 sm:h-3 bg-white/50 hover:bg-white/70'
              } rounded-full`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Play/Pause Button */}
        <button
          onClick={togglePlayPause}
          className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-full flex items-center justify-center transition-colors cursor-pointer"
          aria-label={isPlaying ? 'Pause slideshow' : 'Play slideshow'}
        >
          <i className={`${isPlaying ? 'ri-pause-line' : 'ri-play-line'} text-white text-base sm:text-xl`}></i>
        </button>
      </div>
    </section>
  );
}
